import { createFileRoute, Navigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle, RefreshCw, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useLocaleFormat } from "@/lib/i18n-format";

export const Route = createFileRoute("/_app/admin/email-alerts")({
  component: AdminEmailAlertsPage,
});

type AlertRow = {
  id: string;
  message_id: string;
  recipient_email: string;
  template_name: string;
  error_message: string | null;
  failed_at: string;
  alert_sent_at: string;
  alert_status: string;
};

type DlqRow = {
  message_id: string;
  recipient_email: string;
  template_name: string;
  error_message: string | null;
  created_at: string;
};

function AdminEmailAlertsPage() {
  const { t } = useTranslation();
  const { formatDateTime } = useLocaleFormat();
  const { hasRole, loading } = useAuth();
  const isOwner = hasRole("owner");
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [recentDlq, setRecentDlq] = useState<DlqRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [running, setRunning] = useState(false);

  async function load() {
    setLoadingData(true);
    const [alertsRes, dlqRes] = await Promise.all([
      supabase
        .from("email_auth_alerts")
        .select("*")
        .order("alert_sent_at", { ascending: false })
        .limit(100),
      supabase
        .from("email_send_log")
        .select("message_id, recipient_email, template_name, error_message, created_at")
        .eq("status", "dlq")
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    if (alertsRes.error) {
      toast.error(t("admin.emailAlerts.loadError"), { description: alertsRes.error.message });
    } else {
      setAlerts((alertsRes.data ?? []) as AlertRow[]);
    }
    if (!dlqRes.error) {
      setRecentDlq((dlqRes.data ?? []) as DlqRow[]);
    }
    setLoadingData(false);
  }

  useEffect(() => {
    if (!isOwner) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner]);

  async function runCheckNow() {
    setRunning(true);
    try {
      const res = await fetch("/api/public/hooks/check-email-alerts", { method: "POST" });
      const data = await res.json();
      if (data.ok) {
        toast.success(t("admin.emailAlerts.checkSuccess", { count: data.sent }));
      } else {
        toast.warning(
          t("admin.emailAlerts.checkPartial", {
            error: data.alertError ?? t("admin.emailAlerts.checkUnknownError"),
          }),
        );
      }
      await load();
    } catch (e) {
      toast.error(t("admin.emailAlerts.checkError"));
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!isOwner) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            {t("admin.emailAlerts.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("admin.emailAlerts.subtitle")}</p>
        </div>
        <Button onClick={runCheckNow} disabled={running} variant="outline">
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {t("admin.emailAlerts.checkNow")}
        </Button>
      </div>

      {/* Recent DLQ */}
      <section className="rounded-lg border bg-card">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <Mail className="h-4 w-4" />
              {t("admin.emailAlerts.dlq.title")}
            </h2>
            <p className="text-xs text-muted-foreground">{t("admin.emailAlerts.dlq.subtitle")}</p>
          </div>
          <Badge variant={recentDlq.length > 0 ? "destructive" : "secondary"}>
            {recentDlq.length}{" "}
            {recentDlq.length === 1
              ? t("admin.emailAlerts.dlq.failureOne")
              : t("admin.emailAlerts.dlq.failureOther")}
          </Badge>
        </div>
        {loadingData ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : recentDlq.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {t("admin.emailAlerts.dlq.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="text-left p-3">{t("admin.emailAlerts.dlq.type")}</th>
                  <th className="text-left p-3">{t("admin.emailAlerts.dlq.recipient")}</th>
                  <th className="text-left p-3">{t("admin.emailAlerts.dlq.error")}</th>
                  <th className="text-left p-3">{t("admin.emailAlerts.dlq.when")}</th>
                </tr>
              </thead>
              <tbody>
                {recentDlq.map((r) => (
                  <tr key={r.message_id} className="border-t">
                    <td className="p-3 font-mono text-xs">{r.template_name}</td>
                    <td className="p-3">{r.recipient_email}</td>
                    <td className="p-3 text-destructive text-xs">{r.error_message ?? "—"}</td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {formatDateTime(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Alerts history */}
      <section className="rounded-lg border bg-card">
        <div className="p-4 border-b">
          <h2 className="font-semibold">{t("admin.emailAlerts.history.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("admin.emailAlerts.history.subtitle")}</p>
        </div>
        {loadingData ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {t("admin.emailAlerts.history.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs">
                <tr>
                  <th className="text-left p-3">{t("admin.emailAlerts.history.status")}</th>
                  <th className="text-left p-3">{t("admin.emailAlerts.history.type")}</th>
                  <th className="text-left p-3">
                    {t("admin.emailAlerts.history.affectedRecipient")}
                  </th>
                  <th className="text-left p-3">{t("admin.emailAlerts.history.sentAt")}</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id} className="border-t">
                    <td className="p-3">
                      <Badge variant={a.alert_status === "sent" ? "default" : "destructive"}>
                        {a.alert_status}
                      </Badge>
                    </td>
                    <td className="p-3 font-mono text-xs">{a.template_name}</td>
                    <td className="p-3">{a.recipient_email}</td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {formatDateTime(a.alert_sent_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-muted-foreground text-center">
        <Link to="/dashboard" className="underline">
          {t("admin.emailAlerts.backToDashboard")}
        </Link>
      </p>
    </div>
  );
}
